import { useRef, useState } from "react";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { openSupportChatWithMessage } from "@/core/pylon";
import { useGuide } from "./guide-context";
import { GuideRow } from "./guide-row";
import { ST_ADMIN_GUIDE, ST_PERMISSIONS_GUIDE, type Guide } from "./guides";
import { StLimitedUserSetup } from "./st-limited-user-setup";
import { StepLayout } from "./step-layout";
import { StepNavigation } from "./step-navigation";
import {
  ST_ACCESS_METHOD,
  type OnboardingAnswers,
  type ServiceTitanStage,
  type StAccessMethod,
} from "./types";

/**
 * One ask at a time, rather than three choices at once.
 *
 * Administrator access is what we want and what costs the customer least, so the page
 * asks for that and nothing else. The other two are escapes in small print: one for a
 * customer whose ServiceTitan will not let them, and one for a customer who has no
 * ServiceTitan at all, which is rare but has to be reachable from anywhere.
 *
 * The route is where the customer is standing; the answer is what they have confirmed.
 * They were one value until each route grew a tick box, and then they could not be: an
 * unconfirmed limited user and an unconfirmed admin user are different places to be, and
 * both have no answer yet. The route only decides what is on screen. Leaving with a
 * clear box withdraws any saved confirmation.
 */
const ROUTE = { admin: "admin", limited: "limited", none: "none" } as const;
type Route = (typeof ROUTE)[keyof typeof ROUTE];

// The guide each route is read alongside. Changing route changes which instructions
// apply, so an open panel follows rather than leaving the other branch's steps up.
const ROUTE_GUIDE: Record<Route, Guide | undefined> = {
  admin: ST_ADMIN_GUIDE,
  limited: ST_PERMISSIONS_GUIDE,
  none: undefined,
};

const routeFor = (method: StAccessMethod | undefined): Route => {
  if (method === ST_ACCESS_METHOD.scopedUser) return ROUTE.limited;
  if (method === ST_ACCESS_METHOD.noServiceTitan) return ROUTE.none;
  return ROUTE.admin;
};

/** Only the limited-user branch has anything to send, and only once it is filled in. */
const tenantPayload = (tenantId: string) =>
  tenantId ? { st_credentials_attributes: { tenant_id: tenantId } } : undefined;

const EscapeLink = ({
  children,
  onClick,
}: {
  children: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-sm text-left text-sm text-subtle underline-offset-4 hover:text-ink hover:underline focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-hidden"
  >
    {children}
  </button>
);

export const StAccessScreen = ({
  businessId,
  tenantId,
  stage,
  savedAnswers,
}: {
  businessId: string;
  tenantId: string | null;
  /** How far the connection has got. Only the limited-user branch shows it. */
  stage: ServiceTitanStage;
  savedAnswers: OnboardingAnswers;
}) => {
  const [tenant, setTenant] = useState(tenantId ?? "");
  const [method, setMethod] = useState<StAccessMethod | undefined>(
    savedAnswers.st_access_method,
  );
  // Where they left off, so a returning customer lands on the route they chose.
  const [route, setRoute] = useState<Route>(
    routeFor(savedAnswers.st_access_method),
  );
  const panel = useGuide();
  const card = useRef<HTMLDivElement>(null);
  const limited = route === ROUTE.limited;
  const confirm = (made: boolean, answer: StAccessMethod) =>
    setMethod(made ? answer : undefined);
  // Leaving a route drops an answer that belonged to it, so a half-finished branch
  // cannot be submitted from somewhere else.
  const go = (next: Route, answer?: StAccessMethod) => {
    setRoute(next);
    setMethod(answer);
    // Only swap a guide they already have open. Opening one uninvited would answer a
    // question they have not asked; leaving the old one up answers the wrong one.
    const reading = Object.values(ROUTE_GUIDE).some(
      (guide) => guide !== undefined && guide.id === panel?.open?.id,
    );
    if (reading) panel?.setOpen(ROUTE_GUIDE[next] ?? null);
    // The card's content changes under them, so start at the top of it. Through the
    // element rather than the window: the shell scrolls an inner container, so
    // window.scrollTo is a no-op on this page.
    card.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  // Both branches confirm the user they made. The limited one owes us the tenant as
  // well, because a limited user cannot read it and we cannot find it ourselves.
  const trimmedTenant = tenant.trim();
  const business = limited ? tenantPayload(trimmedTenant) : undefined;
  const missingTenant = limited && !trimmedTenant;

  return (
    <StepLayout
      activePane="servicetitan-access"
      title="Give Volca access to ServiceTitan"
      subtitle="We read your jobs and invoices from ServiceTitan. We never change them."
      nav={
        <StepNavigation
          businessId={businessId}
          step="st_access"
          answers={{ st_access_method: method ?? null }}
          business={business}
          nextDisabled={!method || missingTenant}
          incompletePayload={
            method
              ? undefined
              : { answers: { st_access_method: null }, business }
          }
        />
      }
    >
      <Card ref={card} className="w-full gap-0 overflow-hidden py-0">
        <CardContent className="p-0">
          <Stack className="w-full" gap={0} align="stretch">
            {route === ROUTE.admin && (
              <>
                {/* The guide first. The ask is one sentence; this is the part that
                    takes a customer through ServiceTitan screen by screen. */}
                <GuideRow
                  guide={ST_ADMIN_GUIDE}
                  title="How to do this in ServiceTitan"
                  detail="Step by step, with screenshots"
                />

                <Stack
                  gap={4}
                  align="stretch"
                  className="w-full border-t border-line px-6 py-5"
                >
                  <Stack className="w-full" align="start" gap={1}>
                    <Text variant="title">Give Volca admin access</Text>
                    <Text tone="muted">
                      ServiceTitan emails us the invite, so you don't have to
                      send us anything.
                    </Text>
                  </Stack>

                  <Stack
                    className="w-full"
                    direction="row"
                    gap={2}
                    align="center"
                  >
                    <Checkbox
                      id="st-admin-made"
                      checked={method === ST_ACCESS_METHOD.adminUser}
                      onCheckedChange={(checked) =>
                        confirm(checked === true, ST_ACCESS_METHOD.adminUser)
                      }
                    />
                    <Label htmlFor="st-admin-made">
                      I've made the user and sent the invite
                    </Label>
                  </Stack>
                </Stack>
              </>
            )}

            {limited && (
              <>
                <GuideRow
                  guide={ST_PERMISSIONS_GUIDE}
                  title="What the user needs to see"
                  detail="A tick list you can work through"
                />
                <Stack
                  gap={4}
                  align="stretch"
                  className="w-full border-t border-line px-6 py-5"
                >
                  <Stack className="w-full" align="start" gap={1}>
                    <Text variant="title">Give Volca a limited user</Text>
                    <Text tone="muted">
                      We'll set the connection up, then ask you to approve it.
                    </Text>
                  </Stack>

                  <Stack
                    className="w-full"
                    direction="row"
                    gap={2}
                    align="center"
                  >
                    <Checkbox
                      id="st-limited-made"
                      checked={method === ST_ACCESS_METHOD.scopedUser}
                      onCheckedChange={(checked) =>
                        confirm(checked === true, ST_ACCESS_METHOD.scopedUser)
                      }
                    />
                    <Label htmlFor="st-limited-made">
                      I've made the user and sent the invite
                    </Label>
                  </Stack>

                  <StLimitedUserSetup
                    tenant={tenant}
                    stage={stage}
                    onTenantChange={setTenant}
                  />
                </Stack>
              </>
            )}

            {route === ROUTE.none && (
              <Stack gap={1} align="stretch" className="w-full px-6 py-5">
                <Text variant="title">No ServiceTitan</Text>
                <Text tone="muted">
                  You can continue without ServiceTitan. Volca will contact you
                  if we need information about your systems.
                </Text>
              </Stack>
            )}

            {/* The ways out, kept small and kept together. Whichever route you are on,
                the other two are one click away, so nobody is stuck on an ask they
                cannot answer. */}
            <Stack
              direction="row"
              gap={4}
              align="center"
              className="w-full flex-wrap border-t border-line px-6 py-3"
            >
              {route !== ROUTE.admin && (
                <EscapeLink onClick={() => go(ROUTE.admin)}>
                  Give admin access instead
                </EscapeLink>
              )}
              {!limited && (
                <EscapeLink onClick={() => go(ROUTE.limited)}>
                  I can't give admin access
                </EscapeLink>
              )}
              {route !== ROUTE.none && (
                /* No tick on this one. Saying you have no ServiceTitan is the whole
                   answer, and there is nothing to have done. */
                <EscapeLink
                  onClick={() =>
                    go(ROUTE.none, ST_ACCESS_METHOD.noServiceTitan)
                  }
                >
                  We don't use ServiceTitan
                </EscapeLink>
              )}
              <EscapeLink
                onClick={() =>
                  openSupportChatWithMessage(
                    "I have a question about giving Volca access to ServiceTitan.",
                  )
                }
              >
                Ask us
              </EscapeLink>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </StepLayout>
  );
};
